import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { savedFilterProvider } from '@/providers';
import { appSetNotification } from '@/store';
import { NotificationTypeEnum } from '@/types';
import type { SavedFilter } from '@/types';

import { Loader, MyButton } from '../uiComponents';
import { buildSavedFilterApplyTarget } from './buildSavedFilterApplyTarget';
import { DeleteSavedFilterDialog } from './DeleteSavedFilterDialog';
import { RenameSavedFilterDialog } from './RenameSavedFilterDialog';
import { SavedFiltersEmptyState } from './SavedFiltersEmptyState';
import { SavedFiltersList } from './SavedFiltersList';
import styles from './SavedFilters.module.scss';

type SavedFiltersViewState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'success'; savedFilters: SavedFilter[] };

const getErrorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : 'Unable to load saved filters';

export const SavedFilters = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [viewState, setViewState] = useState<SavedFiltersViewState>({ status: 'loading' });
  const [savedFilterToRename, setSavedFilterToRename] = useState<SavedFilter | null>(null);
  const [savedFilterToDelete, setSavedFilterToDelete] = useState<SavedFilter | null>(null);
  const [isRenaming, setIsRenaming] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const isMountedRef = useRef(true);
  const requestIdRef = useRef(0);
  const renameRequestInFlightRef = useRef(false);
  const deleteRequestInFlightRef = useRef(false);
  const applyNavigationInFlightRef = useRef(false);

  const loadSavedFilters = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    setViewState({ status: 'loading' });

    try {
      const response = await savedFilterProvider.getSavedFilters();

      if (!isMountedRef.current || requestId !== requestIdRef.current) return;

      if (!response.ok || !Array.isArray(response.data)) {
        setViewState({
          status: 'error',
          message: response.message || 'Unable to load saved filters',
        });
        return;
      }

      setViewState({ status: 'success', savedFilters: response.data });
    } catch (error) {
      if (!isMountedRef.current || requestId !== requestIdRef.current) return;

      setViewState({ status: 'error', message: getErrorMessage(error) });
    }
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    void loadSavedFilters();

    return () => {
      isMountedRef.current = false;
    };
  }, [loadSavedFilters]);

  const handleCloseRenameDialog = () => {
    if (renameRequestInFlightRef.current) return;

    setSavedFilterToRename(null);
  };

  const handleApply = (savedFilter: SavedFilter) => {
    if (applyNavigationInFlightRef.current) return;

    applyNavigationInFlightRef.current = true;

    try {
      const target = buildSavedFilterApplyTarget(savedFilter);
      navigate(target);
    } catch (error) {
      applyNavigationInFlightRef.current = false;
      dispatch(
        appSetNotification(
          NotificationTypeEnum.Error,
          error instanceof Error ? error.message : 'Unable to apply saved filter'
        )
      );
    }
  };

  const handleRename = async (savedFilter: SavedFilter, name: string) => {
    if (renameRequestInFlightRef.current) return;

    renameRequestInFlightRef.current = true;
    setIsRenaming(true);

    try {
      const response = await savedFilterProvider.renameSavedFilter(savedFilter.id, { name });

      if (!isMountedRef.current) return;

      if (!response.ok || !response.data) {
        dispatch(
          appSetNotification(
            NotificationTypeEnum.Error,
            response.message || 'Unable to rename saved filter',
            response.status
          )
        );
        return;
      }

      const updatedSavedFilter = response.data;

      setViewState((currentViewState) => {
        if (currentViewState.status !== 'success') return currentViewState;

        return {
          ...currentViewState,
          savedFilters: currentViewState.savedFilters.map((currentSavedFilter) =>
            currentSavedFilter.id === savedFilter.id ? updatedSavedFilter : currentSavedFilter
          ),
        };
      });
      setSavedFilterToRename(null);
      dispatch(
        appSetNotification(NotificationTypeEnum.Success, 'Saved filter was renamed successfully')
      );
    } catch (error) {
      if (!isMountedRef.current) return;

      dispatch(
        appSetNotification(
          NotificationTypeEnum.Error,
          error instanceof Error ? error.message : 'Unable to rename saved filter'
        )
      );
    } finally {
      renameRequestInFlightRef.current = false;
      if (isMountedRef.current) setIsRenaming(false);
    }
  };

  const handleCloseDeleteDialog = () => {
    if (deleteRequestInFlightRef.current) return;

    setSavedFilterToDelete(null);
  };

  const handleDelete = async () => {
    if (!savedFilterToDelete || deleteRequestInFlightRef.current) return;

    const savedFilterId = savedFilterToDelete.id;
    deleteRequestInFlightRef.current = true;
    setIsDeleting(true);

    try {
      const response = await savedFilterProvider.deleteSavedFilter(savedFilterId);

      if (!isMountedRef.current) return;

      if (!response.ok) {
        dispatch(
          appSetNotification(
            NotificationTypeEnum.Error,
            response.message || 'Unable to delete saved filter',
            response.status
          )
        );
        return;
      }

      setViewState((currentViewState) => {
        if (currentViewState.status !== 'success') return currentViewState;

        return {
          ...currentViewState,
          savedFilters: currentViewState.savedFilters.filter(
            (savedFilter) => savedFilter.id !== savedFilterId
          ),
        };
      });
      setSavedFilterToDelete(null);
      dispatch(
        appSetNotification(NotificationTypeEnum.Success, 'Saved filter was deleted successfully')
      );
    } catch (error) {
      if (!isMountedRef.current) return;

      dispatch(
        appSetNotification(
          NotificationTypeEnum.Error,
          error instanceof Error ? error.message : 'Unable to delete saved filter'
        )
      );
    } finally {
      deleteRequestInFlightRef.current = false;
      if (isMountedRef.current) setIsDeleting(false);
    }
  };

  return (
    <div className={styles.container}>
      <header className={styles.title}>
        <h1 className={styles.titleText}>Saved filters</h1>
      </header>

      {viewState.status === 'loading' && (
        <div aria-label='Loading saved filters' className={styles.state} role='status'>
          <Loader />
        </div>
      )}

      {viewState.status === 'error' && (
        <section className={styles.state} role='alert'>
          <h2 className={styles.stateTitle}>Unable to load saved filters</h2>
          <p className={styles.stateDescription}>{viewState.message}</p>
          <MyButton
            data={{
              buttonName: 'Retry',
              buttonType: 'button',
              customWidth: '120px',
              variant: 'contained',
            }}
            onClick={loadSavedFilters}
          />
        </section>
      )}

      {viewState.status === 'success' &&
        (viewState.savedFilters.length ? (
          <SavedFiltersList
            onApply={handleApply}
            onDelete={setSavedFilterToDelete}
            onRename={setSavedFilterToRename}
            savedFilters={viewState.savedFilters}
          />
        ) : (
          <SavedFiltersEmptyState />
        ))}

      <RenameSavedFilterDialog
        isSubmitting={isRenaming}
        onClose={handleCloseRenameDialog}
        onRename={handleRename}
        open={Boolean(savedFilterToRename)}
        savedFilter={savedFilterToRename}
      />
      <DeleteSavedFilterDialog
        isSubmitting={isDeleting}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleDelete}
        open={Boolean(savedFilterToDelete)}
        savedFilter={savedFilterToDelete}
      />
    </div>
  );
};
